import { Badge, Box } from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { WATCHED_FRACTION } from '../../constants/moderation'
import { videoDisplayStyle } from '../../utils/videoDisplay'

// The video itself: drag to scrub, loops inside its trim window, and reports
// when it has been watched.
//
// Watched means the playhead has covered the trim window in one continuous
// run — measured as how far it has travelled since the last scrub, not as
// accumulated playing time. The difference matters on a busy machine: a wall of
// videos playing at once stutters, and a rule that only counted small smooth
// steps would decide such a clip had never been watched and lock its buttons
// forever. Travel survives stutter; a scrub resets the baseline to wherever it
// landed, so dragging to the end still earns nothing.

export function VideoSurface({
  src,
  challenge,
  challengeColor,
  challengeTextColor,
  trimStart,
  trimEnd,
  rotation,
  forceVertical,
  onWatched,
  trackWatch = true,
  graceMs,
}) {
  const videoRef = useRef(null)
  const scrubbing = useRef(false)
  const lap = useRef({ from: null, max: null })
  const reported = useRef(false)
  const [progress, setProgress] = useState(0)
  const [unverified, setUnverified] = useState(false)
  const tStart = trimStart || 0

  // Held in a ref rather than closed over: the parent passes a fresh arrow every
  // render, and a `report` whose identity changed with it would restart the
  // grace timeout below on every render, so it could never fire.
  const onWatchedRef = useRef(onWatched)
  useEffect(() => {
    onWatchedRef.current = onWatched
  }, [onWatched])

  const report = useCallback((verified, why) => {
    if (reported.current) return
    reported.current = true
    if (!verified) setUnverified(true)
    onWatchedRef.current?.(verified, why)
  }, [])

  // The stored trim window is not always inside the clip. Durations recorded by
  // MediaRecorder frequently never make it into the container, so an upload can
  // carry a trimEnd past the last frame that exists. Measured against that, the
  // playhead can never reach the end and the video is unwatchable — and so,
  // with the gate on, undecidable. Everything below works off the clip's real
  // extent instead.
  const bounds = useCallback(() => {
    const video = videoRef.current
    const duration = video && Number.isFinite(video.duration) ? video.duration : null
    if (!duration) return { start: tStart, end: trimEnd || null }
    const start = Math.min(tStart, Math.max(0, duration - 0.1))
    const end = Math.min(trimEnd || duration, duration)
    return { start, end: end > start ? end : duration }
  }, [tStart, trimEnd])

  const onTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    const t = video.currentTime
    const { start, end } = bounds()

    if (trackWatch && !scrubbing.current) {
      const l = lap.current
      const windowSec = end && end > start ? end - start : 0
      const wrapped = l.from != null && t < (l.max ?? 0) - 0.25

      if (wrapped) {
        // The playhead only goes backwards when the clip reached its end and
        // looped. That is proof of a full play-through, and it is proof the
        // ratio below cannot supply on its own: timeupdate fires about four
        // times a second, so on a two-second clip the last frames are never
        // sampled and coverage tops out short of the threshold.
        if (windowSec && l.from <= start + 0.1 * windowSec) {
          setProgress(1)
          report(true, 'looped')
        }
        l.from = t
        l.max = t
      } else if (l.from == null) {
        l.from = t
        l.max = t
      } else {
        l.max = Math.max(l.max, t)
      }

      if (windowSec) {
        const ratio = Math.min(1, (l.max - l.from) / windowSec)
        setProgress((prev) => (reported.current ? prev : ratio))
        if (ratio >= WATCHED_FRACTION) report(true, 'played-through')
      }
    }

    // Trim bounds: loop within the window. Done after the measurement so the
    // last frame of the window is counted before the wrap.
    if (end && t >= end - 0.05) video.currentTime = start
    else if (t < start) video.currentTime = start
  }, [bounds, trackWatch, report])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const handleLoaded = () => {
      const { start } = bounds()
      if (start > 0) video.currentTime = start
    }
    // A clip that cannot be decoded still has to be judgeable, or it sits in
    // the queue forever being handed from one moderator to the next.
    const handleError = () => report(false, 'media-error')
    const handleEnded = () => report(true, 'ended')
    video.addEventListener('loadedmetadata', handleLoaded)
    video.addEventListener('timeupdate', onTimeUpdate)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)
    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded)
      video.removeEventListener('timeupdate', onTimeUpdate)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('error', handleError)
    }
  }, [bounds, onTimeUpdate, report])

  // The same escape hatch for a clip that never errors but never progresses
  // either — a stalled download, a stream that yields no frames.
  useEffect(() => {
    if (!trackWatch || !graceMs) return
    const id = setTimeout(() => report(false, 'no-progress'), graceMs)
    return () => clearTimeout(id)
  }, [trackWatch, graceMs, report])

  const setScrubPosition = (e) => {
    const video = videoRef.current
    if (!video || !video.duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const { start, end } = bounds()
    video.currentTime = start + (x / rect.width) * (end - start)
    // Whatever was skipped over does not count: the lap restarts here.
    lap.current = { from: video.currentTime, max: video.currentTime }
  }

  const handlePointerDown = (e) => {
    scrubbing.current = true
    videoRef.current?.pause()
    setScrubPosition(e)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e) => {
    if (scrubbing.current) setScrubPosition(e)
  }

  const handlePointerUp = () => {
    scrubbing.current = false
    videoRef.current?.play().catch(() => {})
  }

  const barColor = unverified
    ? 'var(--mc-amber)'
    : progress >= WATCHED_FRACTION
      ? 'var(--mc-green)'
      : 'var(--mc-text-muted)'

  return (
    <Box
      position="absolute"
      inset="0"
      cursor="ew-resize"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <video
        ref={videoRef}
        src={src}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          position: 'absolute',
          inset: 0,
          ...videoDisplayStyle(rotation, forceVertical),
        }}
        autoPlay
        loop
        muted
        playsInline
      />

      {challenge && (
        <Badge
          position="absolute"
          top="3px"
          left="3px"
          bg={challengeColor || 'whiteAlpha.300'}
          color={challengeTextColor || 'white'}
          fontSize="0.6rem"
          px={1.5}
          borderRadius="sm"
          pointerEvents="none"
        >
          {challenge}
        </Badge>
      )}

      {/* Watch progress. At the very bottom so it reads as a fill line for the
          tile rather than another control competing for space. Amber means the
          buttons opened without a verified play-through. */}
      {trackWatch && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          h="3px"
          bg="blackAlpha.600"
          pointerEvents="none"
        >
          <Box
            h="100%"
            w={unverified ? '100%' : `${Math.round(progress * 100)}%`}
            bg={barColor}
            transition="width 0.2s linear"
          />
        </Box>
      )}
    </Box>
  )
}
